/* ==========================================================================
   cr-subscribe.js — the subscribe-for-free-shipping popup.

   WHAT IT IS FOR. Until 2026-09-29 the shop gave free shipping to the whole
   world automatically and got nothing back — no email, no relationship, and
   on a shop with no traffic that is the one thing worth collecting. The
   freight is now charged, and subscribing gives it back.

   NATALIE'S THREE RULES, and each one is a line of code here:
     "הפופאפ רק בהוספה לעגלה"  -> it never fires on page load. Only after
        someone has actually put something in the cart, which is the moment
        they have decided to buy and the freight is about to appear.
     "subscribe", never "join the list" -> her wording, throughout.
     A returning subscriber must not be asked again -> see REMEMBERING below.

   HOW THE DISCOUNT IS ACTUALLY APPLIED, since this is the part that looks
   like magic: Shopify serves /discount/<CODE>, which sets the code on the
   session and redirects. So the popup posts the email, then sends the browser
   through that URL and back to where it was. Nobody types a code, and nobody
   waits for an email to arrive.

   REMEMBERING, AND WHY IT IS TIED TO THE EMAIL AND NOT TO THE VISIT. The flag
   below is written ONLY when someone submits an address. Writing it on a
   second visit instead would hand free shipping to a person who gave us
   nothing, which is the whole point undone. Her concern was the opposite one
   and it is the right one: a loyal customer must not be asked over and over.
   So once the flag is set the popup never appears again on that browser.
   The flag is the browser's claim and not proof — it can be cleared, and a
   different device looks new. The worst case is that someone gets free
   shipping, which is exactly where the shop was yesterday.

   ALREADY A SUBSCRIBER AND TYPING THE ADDRESS AGAIN? Shopify cannot be asked
   from a shop page whether an address is on the list — that is blocked on
   purpose, because it would let anyone test whether a person is a customer.
   So the reply is written to be true either way, and the discount is applied
   from the redirect rather than from whether the signup was new. A returning
   subscriber is never told "you already did this" and never left without the
   shipping.
   ========================================================================== */
(function () {
  'use strict';

  var CODE = 'WELCOMEFRIEND';
  var SEEN = 'cr-subscribed';      // set only on a real submit
  var SHOWN = 'cr-sub-shown';      // once per visit, sessionStorage

  function remembered(store, key) {
    try { return store.getItem(key) === '1'; } catch (e) { return false; }
  }
  function remember(store, key) {
    try { store.setItem(key, '1'); } catch (e) { /* private window */ }
  }

  /* A subscriber is never asked again. */
  function alreadyIn() {
    return remembered(window.localStorage, SEEN);
  }

  var root = null;

  function build() {
    if (root) return root;
    root = document.createElement('div');
    root.className = 'cr-sub';
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-label', 'Free shipping for subscribers');
    root.innerHTML = [
      '<div class="cr-sub__back" data-close></div>',
      '<div class="cr-sub__box">',
      '  <button class="cr-sub__x" type="button" data-close aria-label="Close">&times;</button>',
      '  <div data-step="ask">',
      '    <h2 class="cr-sub__title">Shipping is on us</h2>',
      '    <p class="cr-sub__text">Subscribe and your shipping is free, worldwide, on this order and every one after it. You will also get new designs before anyone else, and an invitation when we open an evening of coffee calls.</p>',
      '    <form class="cr-sub__form" novalidate>',
      '      <label class="cr-sub__label" for="cr-sub-email">Email</label>',
      '      <input class="cr-sub__input" id="cr-sub-email" type="email" name="email" autocomplete="email" required placeholder="you@example.com">',
      '      <button class="cr-sub__go" type="submit">Subscribe and take the free shipping</button>',
      '      <p class="cr-sub__err" hidden>That address does not look right.</p>',
      '    </form>',
      '    <button class="cr-sub__no" type="button" data-close>No thanks, I will pay the shipping</button>',
      '  </div>',
      '  <div data-step="done" hidden>',
      '    <h2 class="cr-sub__title">You are in</h2>',
      '    <p class="cr-sub__text">Shipping is on us. Putting it on your order now.</p>',
      '  </div>',
      '</div>'
    ].join('');
    document.body.appendChild(root);

    root.addEventListener('click', function (e) {
      if (e.target.hasAttribute && e.target.hasAttribute('data-close')) close();
    });
    root.querySelector('.cr-sub__form').addEventListener('submit', submit);
    return root;
  }

  function open() {
    build().classList.add('is-open');
    document.addEventListener('keydown', onKey);
    var f = root.querySelector('.cr-sub__input');
    if (f) setTimeout(function () { f.focus(); }, 60);
  }

  function close() {
    if (root) root.classList.remove('is-open');
    document.removeEventListener('keydown', onKey);
  }

  function onKey(e) { if (e.key === 'Escape') close(); }

  function submit(e) {
    e.preventDefault();
    var input = root.querySelector('.cr-sub__input');
    var err = root.querySelector('.cr-sub__err');
    var email = (input.value || '').trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      err.hidden = false;
      input.focus();
      return;
    }
    err.hidden = true;

    /* Shopify's own newsletter signup. form_type=customer with the newsletter
       tag is what the footer form posts; doing it by hand keeps the popup
       from needing a Liquid-rendered form inside it. */
    var body = new URLSearchParams();
    body.set('form_type', 'customer');
    body.set('utf8', '✓');
    body.set('contact[email]', email);
    body.set('contact[tags]', 'newsletter');

    var go = root.querySelector('.cr-sub__go');
    go.disabled = true;
    go.textContent = 'One moment...';

    /* The redirect happens WHETHER OR NOT the signup call succeeds. A person
       who is already a subscriber, or whose signup errors for any reason,
       still gets the shipping they were promised — being told "something went
       wrong" after handing over an address is the one outcome worth ruling
       out. */
    function onwards() {
      remember(window.localStorage, SEEN);
      root.querySelector('[data-step="ask"]').hidden = true;
      root.querySelector('[data-step="done"]').hidden = false;
      var back = window.location.pathname + window.location.search;
      window.location.href = '/discount/' + CODE +
        '?redirect=' + encodeURIComponent(back);
    }

    fetch('/contact', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString()
    }).then(onwards).catch(onwards);
  }

  /* ---- the trigger: a successful add to cart, and nothing else ----------
     Wrapping fetch catches the product form, quick add and the cart drawer
     alike, which is the same approach cr-offer.js uses (D-308). The popup is
     shown AFTER the request resolves, so it never appears over a failed add. */
  var nativeFetch = window.fetch;
  window.fetch = function () {
    var url = arguments[0];
    var path = typeof url === 'string' ? url : (url && url.url) || '';
    var adding = path.indexOf('/cart/add') !== -1;
    var out = nativeFetch.apply(this, arguments);
    if (!adding) return out;
    return out.then(function (res) {
      try {
        if (res && res.ok && !alreadyIn() &&
            !remembered(window.sessionStorage, SHOWN)) {
          remember(window.sessionStorage, SHOWN);
          setTimeout(open, 450);
        }
      } catch (e) { /* never break an add to cart */ }
      return res;
    });
  };
})();
